import { jobLists } from "character";
import FormComponents from "components/forms/FormComponents";
import Link from "components/link/Link";
import NoCharacter from "components/no-character/NoCharacter";
import { items } from "data-files/items";
import { quests } from "data-files/quests";
import { EorzeaDataType, GameData, InputType, LinkType, RelicData, View } from "enums";
import manager from "managers/app";
import { ViewRelicInfo } from "managers/types/relic-types";
import { Component } from "react";
import { connect } from "react-redux";
import t from "translations/translator";
import { AppState } from "types/app";
import utils from "util/utils";
import ContentHeader from "views/page-parts/content/content-header/ContentHeader";
import DetailsSection from "views/page-parts/content/details-section/DetailsSection";
import Obtained from "views/page-parts/content/obtained/Obtained";
import OverviewTable from "views/page-parts/content/overview-table/OverviewTable";
import { Requirement } from "views/page-parts/content/relic-requirements/types";
import { ViewWeaponZodiacAtmaProps } from "views/relics/weapons/zodiac/atma/types";
import { TaskInfo } from "views/type";
import "./WeaponZodiacAtma.css";

const mapStateToProps = (state:AppState) => {
	return {
		lang: state.userInfo.options.lang,
		gameLang: state.userInfo.options.gameLang,
		userInfo: state.userInfo,
		hideCompletedSteps: state.userInfo.options.hideCompletedSteps,
		hideCompletedTasks: state.userInfo.options.hideCompletedTasks,
		showJob: state.userInfo.options.job,
	};
};

class WeaponZodiacAtma extends Component<ViewWeaponZodiacAtmaProps> {
	private requirements:Requirement[] = [];
	private importantNPCs:GameData.NPC[] = [];
	private relicInfo:ViewRelicInfo = {
		jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC],
		type: RelicData.Type.WEAPON,
		relic: RelicData.Group.WEAPON_ZODIAC,
		part: RelicData.Part.WEAPON_ATMA,
		steps: [RelicData.Step.ARMS, RelicData.Step.ATMAS],
		stepPlaceholders: {
			[RelicData.Step.ARMS]: { QUEST: GameData.Quest.UP_IN_ARMS },
			[RelicData.Step.ATMAS]: { ITEM: RelicData.Part.WEAPON_ATMA },
		},
		next: {
			part: RelicData.Part.WEAPON_ANIMUS,
			view: View.RELIC_WEAPONS_ZODIAC_ANIMUS,
		},
		previous: {
			part: RelicData.Part.WEAPON_ZENITH,
			view: View.RELIC_WEAPONS_ZODIAC_ZENITH,
		},
	};

	constructor(props:ViewWeaponZodiacAtmaProps){
		super(props);
		this.requirements = this.getRequirements();
		this.importantNPCs = this.getImportantNPCs();
	}

	render():JSX.Element{
		const character = manager.data.getActiveCharacter();
		let row = "odd-row";

		if(character === null){ return <NoCharacter />; }

		const completeSteps = manager.content.getCompleteSteps(character.progress, this.relicInfo);

		const taskInfo:TaskInfo[] = [
			// Note: Job doesn't matter in getTaskItem (all jobs need the same item)
			{ task: RelicData.Task.TASK_1, target: manager.relics.getTaskItem(RelicData.Step.ATMAS, RelicData.Task.TASK_1) },
			{ task: RelicData.Task.TASK_2, target: manager.relics.getTaskItem(RelicData.Step.ATMAS, RelicData.Task.TASK_2) },
			{ task: RelicData.Task.TASK_3, target: manager.relics.getTaskItem(RelicData.Step.ATMAS, RelicData.Task.TASK_3) },
			{ task: RelicData.Task.TASK_4, target: manager.relics.getTaskItem(RelicData.Step.ATMAS, RelicData.Task.TASK_4) },
			{ task: RelicData.Task.TASK_5, target: manager.relics.getTaskItem(RelicData.Step.ATMAS, RelicData.Task.TASK_5) },
			{ task: RelicData.Task.TASK_6, target: manager.relics.getTaskItem(RelicData.Step.ATMAS, RelicData.Task.TASK_6) },
			{ task: RelicData.Task.TASK_7, target: manager.relics.getTaskItem(RelicData.Step.ATMAS, RelicData.Task.TASK_7) },
			{ task: RelicData.Task.TASK_8, target: manager.relics.getTaskItem(RelicData.Step.ATMAS, RelicData.Task.TASK_8) },
			{ task: RelicData.Task.TASK_9, target: manager.relics.getTaskItem(RelicData.Step.ATMAS, RelicData.Task.TASK_9) },
			{ task: RelicData.Task.TASK_10, target: manager.relics.getTaskItem(RelicData.Step.ATMAS, RelicData.Task.TASK_10) },
			{ task: RelicData.Task.TASK_11, target: manager.relics.getTaskItem(RelicData.Step.ATMAS, RelicData.Task.TASK_11) },
			{ task: RelicData.Task.TASK_12, target: manager.relics.getTaskItem(RelicData.Step.ATMAS, RelicData.Task.TASK_12) },
		];

		return (
			<div id="weapon-zodiac-atma" className="relic">
				<ContentHeader requirements={this.requirements} importantNPCs={this.importantNPCs} relicInfo={this.relicInfo}>
					<p>{t.t("The %PART% upgrade follows on from %PREVIOUSPART%", { PART: this.relicInfo.part, PREVIOUSPART: this.relicInfo.previous ? this.relicInfo.previous.part : "Unknown"})}</p>
					<p><Link type={LinkType.LODESTONE} lodestoneType={EorzeaDataType.BLUEQUEST} text={GameData.Quest.UP_IN_ARMS} lodestoneID={quests[GameData.Quest.UP_IN_ARMS].lodestoneID} /> {t.t("is only required for the first job")}</p>
				</ContentHeader>
				<div id="relic-checklist">
					<OverviewTable character={character} completeSteps={completeSteps} relicInfo={this.relicInfo} />
				</div>
				<div id="relic-details">
					<h1>{t.t("Details")}</h1>
					{this.props.hideCompletedSteps && completeSteps.allComplete ? t.t("All Steps Complete") : null }
					<DetailsSection title={RelicData.Step.ARMS} placeholders={this.relicInfo.stepPlaceholders[RelicData.Step.ARMS]} id={RelicData.Step.ARMS} stepComplete={completeSteps[RelicData.Step.ARMS]}><p>{t.t("Complete")}<Link type={LinkType.LODESTONE} lodestoneType={quests[GameData.Quest.UP_IN_ARMS].type} text={quests[GameData.Quest.UP_IN_ARMS].name} lodestoneID={quests[GameData.Quest.UP_IN_ARMS].lodestoneID} /></p></DetailsSection>
					<DetailsSection title={RelicData.Step.ATMAS} placeholders={this.relicInfo.stepPlaceholders[RelicData.Step.ATMAS]} id={RelicData.Step.ATMAS} stepComplete={completeSteps[RelicData.Step.ATMAS]}>
						<div className="table-wrapper">
							<table className="table">
								<thead><tr><th>{t.t("Obtained")}</th><th>{t.t("Item")}</th><th>{t.t("Quantity / Job")}</th><th>{t.t("Total")}</th><th>{t.t("Used")}</th><th>{t.t("Remaining")}</th><th>{t.t("Inventory")}</th><th>{t.t("To Obtain")}</th></tr></thead>
								<tbody>
									{taskInfo.map((thisTask) => {
										let thisTarget = GameData.Item.UNKNOWN;
										if(utils.guards.isEnumValue(GameData.Item, thisTask.target)){ thisTarget = thisTask.target; }

										const inventory = manager.data.getInventoryStatus({
											item: thisTarget,
											relic: [this.relicInfo.type, this.relicInfo.relic, this.relicInfo.part, RelicData.Step.ATMAS, thisTask.task],
										});

										if(this.props.hideCompletedTasks && inventory.toObtain === 0){ return null; }

										const tableRow = <tr key={`${RelicData.Step.ATMAS}-${thisTask.task}`} className={row}>
											<td><Obtained obtained={items[thisTarget].obtained} qtyNeeded={inventory.total} /></td>
											<td>{t.t(thisTarget)}</td>
											<td>{inventory.qtyPerJob.toLocaleString()}</td>
											<td>{inventory.total.toLocaleString()}</td>
											<td>{inventory.used.toLocaleString()}</td>
											<td>{inventory.remaining.toLocaleString()}</td>
											<td><FormComponents.TextInput label={{ hide: true }} input={{ type: InputType.NUMBER, value: inventory.inventory, dataAttributes: { item: thisTarget }, events: { onChange: manager.content.inventoryUpdated }}} /></td>
											<td>{inventory.toObtain.toLocaleString()}</td>
										</tr>;
										row = row === "odd-row" ? "even-row" : "odd-row";
										return tableRow;
									})}
									{
										character.progress.isComplete(this.relicInfo.type, this.relicInfo.relic, this.relicInfo.part, RelicData.Step.ATMAS)
											? <tr className="all-complete"><td colSpan={8}><p>{t.t("All Complete")}</p></td></tr>
											: null
									}
								</tbody>
							</table>
						</div>
					</DetailsSection>
				</div>
			</div>
		);
	}

	componentDidMount(){
		manager.view.changeComplete();
	}

	private getRequirements():Requirement[]{
		if(!this.relicInfo || this.relicInfo.previous === null){ return []; }
		return [
			{ type: "previous part", part: this.relicInfo.previous.part, partView: this.relicInfo.previous.view },
		];
	}

	private getImportantNPCs():GameData.NPC[]{
		return [GameData.NPC.GEROLT_NORTH_SHROUD, GameData.NPC.JALZAHN];
	}
}
export default connect(mapStateToProps)(WeaponZodiacAtma);
